import { useCallback, useEffect, useRef, useState } from 'react';

import type { LoadState } from '../../components/workflowShared';
import type {
  AccountUser,
  GitHubInstallationSummary,
  Workspace,
  WorkspaceScope
} from '../../components/screens/RunScreens';
import {
  DEV_GITHUB_SESSION_ID,
  runClearGitHubCallbackParams,
  runClearStoredAccountState,
  runGetDevAccountUser,
  runGetDevGitHubInstallations,
  runGetDevGitHubLogin,
  runGetDevWorkspaces,
  runGetInitialGitHubSessionId,
  runGetStoredWorkspaceId,
  runIsDevGitHubAccountEnabled,
  runRequestJson,
  runStoreGitHubSessionId,
  runStoreWorkspaceId,
  type GitHubAppConfigResponse,
  type GitHubInstallationsResponse,
  type GitHubSessionResponse
} from './appRunData';

export type AppGitHubState = 'disconnected' | 'loading' | 'needs-install' | 'connected';

const DEFAULT_WORKSPACE_NAME = 'My workspace';

const EMAIL_PATTERN = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/;

/** Mirrors the backend check for the account email that receives pentest results. */
export const isValidAccountEmail = (value: string): boolean => {
  return value.length <= 254 && EMAIL_PATTERN.test(value);
};

const toSlug = (name: string): string => {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'workspace';
};

export function useAppAccount() {
  const [githubSessionId, setGithubSessionId] = useState(() => runGetInitialGitHubSessionId());
  const [githubConfig, setGithubConfig] = useState<GitHubAppConfigResponse | null>(null);
  const [githubConfigState, setGithubConfigState] = useState<LoadState>('idle');
  const [githubLogin, setGithubLogin] = useState('');
  const [githubInstallations, setGithubInstallations] = useState<GitHubInstallationSummary[]>([]);
  const [githubSessionState, setGithubSessionState] = useState<LoadState>('idle');
  const [githubRefreshKey, setGithubRefreshKey] = useState(0);
  const [accountUser, setAccountUser] = useState<AccountUser | null>(null);
  const [accountState, setAccountState] = useState<LoadState>('loading');
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspacesState, setWorkspacesState] = useState<LoadState>('idle');
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState(() => runGetStoredWorkspaceId());
  const workspacesRef = useRef(workspaces);

  workspacesRef.current = workspaces;

  const isDev = runIsDevGitHubAccountEnabled();

  useEffect(() => {
    if (isDev) {
      setGithubConfig({ configured: true, connectUrl: '', installUrl: '' });
      setGithubConfigState('ready');
      return;
    }

    setGithubConfigState('loading');
    runRequestJson<GitHubAppConfigResponse>('/github/app')
      .then((config) => {
        setGithubConfig(config);
        setGithubConfigState('ready');
      })
      .catch((error: unknown) => {
        console.error('Failed to load GitHub App config:', error);
        setGithubConfigState('error');
      });
  }, [isDev]);

  useEffect(() => {
    runClearGitHubCallbackParams();

    if (!githubSessionId) {
      setGithubLogin('');
      setGithubInstallations([]);
      setGithubSessionState('idle');
      return undefined;
    }

    runStoreGitHubSessionId(githubSessionId);

    if (githubSessionId === DEV_GITHUB_SESSION_ID && isDev) {
      setGithubLogin(runGetDevGitHubLogin());
      setGithubInstallations(runGetDevGitHubInstallations());
      setGithubSessionState('ready');
      return undefined;
    }

    const encodedSessionId = encodeURIComponent(githubSessionId);
    let cancelled = false;

    // A refresh keeps the current state visible instead of flashing a loading state.
    setGithubSessionState((current) => current === 'ready' ? current : 'loading');
    Promise.all([
      runRequestJson<GitHubSessionResponse>(`/github/session/${encodedSessionId}`),
      runRequestJson<GitHubInstallationsResponse>(`/github/session/${encodedSessionId}/installations`)
    ])
      .then(([session, installations]) => {
        if (cancelled) {
          return;
        }

        setGithubLogin(session.login ?? '');
        setGithubInstallations(installations.installations ?? []);
        setGithubSessionState('ready');
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }

        console.error('Failed to load GitHub session:', error);
        setGithubLogin('');
        setGithubInstallations([]);
        setGithubSessionState('error');
        runStoreGitHubSessionId('');
      });

    return () => {
      cancelled = true;
    };
  }, [githubSessionId, githubRefreshKey, isDev]);

  useEffect(() => {
    if (isDev) {
      setAccountUser(runGetDevAccountUser());
      setAccountState('ready');
      return;
    }

    runRequestJson<AccountUser>('/auth/me')
      .then((user) => setAccountUser(user))
      .catch(() => setAccountUser(null))
      .finally(() => setAccountState('ready'));
  }, [isDev, githubSessionId]);

  // Keyed on the id so editing account details (such as the email) does not reload workspaces.
  const accountUserId = accountUser?.id;

  useEffect(() => {
    if (!accountUserId) {
      setWorkspaces([]);
      setWorkspacesState('idle');
      return;
    }

    const applyWorkspaces = (loaded: Workspace[]) => {
      setWorkspaces(loaded);
      setWorkspacesState('ready');
      setCurrentWorkspaceId((currentId) => {
        return loaded.some((workspace) => workspace.id === currentId) ? currentId : loaded[0]?.id ?? '';
      });
    };

    if (isDev) {
      applyWorkspaces(runGetDevWorkspaces());
      return;
    }

    setWorkspacesState('loading');
    runRequestJson<{ workspaces?: Workspace[] }>('/workspaces')
      .then((response) => applyWorkspaces(response.workspaces ?? []))
      .catch((error: unknown) => {
        console.error('Failed to load workspaces:', error);
        setWorkspaces([]);
        setWorkspacesState('error');
      });
  }, [accountUserId, isDev]);

  useEffect(() => {
    runStoreWorkspaceId(currentWorkspaceId);
  }, [currentWorkspaceId]);

  // GitHub repository access and an application login are separate sessions.
  const isSignedIn = Boolean(accountUser);
  const githubState: AppGitHubState = githubSessionState === 'loading'
    ? 'loading'
    : githubSessionState !== 'ready'
      ? 'disconnected'
      : githubInstallations.length > 0 ? 'connected' : 'needs-install';

  // Repository access is granted on GitHub in another tab; pick it up when the user comes back.
  useEffect(() => {
    if (githubState !== 'needs-install') {
      return undefined;
    }

    const refresh = () => setGithubRefreshKey((key) => key + 1);

    window.addEventListener('focus', refresh);

    return () => window.removeEventListener('focus', refresh);
  }, [githubState]);

  const createWorkspace = useCallback(async (name: string): Promise<Workspace | null> => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return null;
    }

    try {
      const workspace = isDev
        ? {
          ...runGetDevWorkspaces()[0],
          id: `dev-workspace-${Date.now()}`,
          name: trimmedName,
          slug: toSlug(trimmedName),
          scope: undefined
        }
        : (await runRequestJson<{ workspace: Workspace }>('/workspaces', {
          method: 'POST',
          body: JSON.stringify({ name: trimmedName })
        })).workspace;

      workspacesRef.current = [workspace, ...workspacesRef.current];
      setWorkspaces((current) => [workspace, ...current]);
      setCurrentWorkspaceId(workspace.id);

      return workspace;
    } catch (error) {
      console.error('Failed to create workspace:', error);
      return null;
    }
  }, [isDev]);

  /** Returns the current workspace, creating a first one when the account has none. */
  const ensureWorkspace = useCallback(async (): Promise<Workspace | null> => {
    const existing = workspacesRef.current.find((workspace) => workspace.id === currentWorkspaceId)
      ?? workspacesRef.current[0];

    return existing ?? createWorkspace(DEFAULT_WORKSPACE_NAME);
  }, [createWorkspace, currentWorkspaceId]);

  const saveWorkspaceScope = useCallback(async (workspaceId: string, scope: WorkspaceScope): Promise<Workspace> => {
    const current = workspacesRef.current.find((workspace) => workspace.id === workspaceId);

    if (!current) {
      throw new Error('Workspace not found.');
    }

    const updated = isDev
      ? { ...current, scope }
      : (await runRequestJson<{ workspace: Workspace }>(`/workspaces/${encodeURIComponent(workspaceId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ scope })
      })).workspace;

    workspacesRef.current = workspacesRef.current.map((workspace) => workspace.id === workspaceId ? updated : workspace);
    setWorkspaces((list) => list.map((workspace) => workspace.id === workspaceId ? updated : workspace));

    return updated;
  }, [isDev]);

  const renameWorkspace = useCallback(async (workspaceId: string, name: string): Promise<boolean> => {
    const trimmedName = name.trim();
    const current = workspacesRef.current.find((workspace) => workspace.id === workspaceId);

    if (!trimmedName || !current) {
      return false;
    }

    try {
      const updated = isDev
        ? { ...current, name: trimmedName, slug: toSlug(trimmedName), updatedAt: new Date().toISOString() }
        : (await runRequestJson<{ workspace: Workspace }>(`/workspaces/${encodeURIComponent(workspaceId)}`, {
          method: 'PATCH',
          body: JSON.stringify({ name: trimmedName })
        })).workspace;

      workspacesRef.current = workspacesRef.current.map((workspace) => workspace.id === workspaceId ? updated : workspace);
      setWorkspaces(workspacesRef.current);
      return true;
    } catch (error) {
      console.error('Failed to rename workspace:', error);
      return false;
    }
  }, [isDev]);

  const deleteWorkspace = useCallback(async (workspaceId: string): Promise<boolean> => {
    if (!workspacesRef.current.some((workspace) => workspace.id === workspaceId)) {
      return false;
    }

    try {
      if (!isDev) {
        await runRequestJson(`/workspaces/${encodeURIComponent(workspaceId)}`, { method: 'DELETE' });
      }

      const next = workspacesRef.current.filter((workspace) => workspace.id !== workspaceId);

      workspacesRef.current = next;
      setWorkspaces(next);
      setCurrentWorkspaceId((current) => current === workspaceId ? next[0]?.id ?? '' : current);
      return true;
    } catch (error) {
      console.error('Failed to delete workspace:', error);
      return false;
    }
  }, [isDev]);

  const getWorkspace = useCallback((workspaceId: string): Workspace | undefined => {
    return workspacesRef.current.find((workspace) => workspace.id === workspaceId);
  }, []);

  const connectGitHub = useCallback(() => {
    if (!githubConfig?.connectUrl) {
      return;
    }

    const returnTo = `${window.location.pathname}${window.location.search}`;

    window.location.href = `${githubConfig.connectUrl}?returnTo=${encodeURIComponent(returnTo)}`;
  }, [githubConfig?.connectUrl]);

  /** Opens the GitHub App install page, where repository access is granted or changed. */
  const manageGitHubAccess = useCallback(() => {
    if (githubConfig?.installUrl) {
      window.open(githubConfig.installUrl, '_blank', 'noopener');
    }
  }, [githubConfig?.installUrl]);

  /** Saves the email pentest results are sent to. Throws with a user-facing message on failure. */
  const updateEmail = useCallback(async (value: string): Promise<void> => {
    const email = value.trim();

    if (!isValidAccountEmail(email)) {
      throw new Error('Enter a valid email address.');
    }

    if (isDev) {
      setAccountUser((current) => current ? { ...current, email } : current);
      return;
    }

    const response = await runRequestJson<{ user: AccountUser }>('/user', {
      method: 'PATCH',
      body: JSON.stringify({ email })
    });

    setAccountUser((current) => current ? { ...current, email: response.user.email } : current);
  }, [isDev]);

  /** Resolves to whether the session was ended. */
  const logout = useCallback(async (): Promise<boolean> => {
    try {
      if (!isDev) {
        await runRequestJson<undefined>('/auth/logout', { method: 'POST' });
      }
    } catch (error) {
      console.error('Failed to log out:', error);
      return false;
    }

    runClearStoredAccountState();
    setGithubSessionId('');
    setAccountUser(null);
    setCurrentWorkspaceId('');

    return true;
  }, [isDev]);

  return {
    accountUser,
    accountState,
    isSignedIn,
    githubConfig,
    githubConfigState,
    githubSessionId,
    githubInstallations,
    githubLogin,
    githubState,
    workspaces,
    workspacesState,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    createWorkspace,
    ensureWorkspace,
    saveWorkspaceScope,
    renameWorkspace,
    deleteWorkspace,
    getWorkspace,
    connectGitHub,
    manageGitHubAccess,
    updateEmail,
    logout
  };
}
